import React, { useEffect, useState } from 'react';
import api from '../../lib/api';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Button } from '../../components/ui/button';
import { FileText, CheckCircle, XCircle, Clock, Eye } from 'lucide-react';
import { toast } from 'sonner';

export default function AdmissionsManagement() {
  const [apps, setApps] = useState([]);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterDiv, setFilterDiv] = useState('');

  const load = () => {
    const params = new URLSearchParams();
    if (filterStatus) params.set('status', filterStatus);
    if (filterDiv) params.set('division', filterDiv);
    api.get(`/admissions/applications?${params}`).then(r => setApps(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [filterStatus, filterDiv]);

  const updateStatus = async (id, status, notes = '') => {
    await api.put(`/admissions/applications/${id}`, { status, admin_notes: notes });
    toast.success(`Application ${status}`);
    load();
  };

  const statusIcons = { submitted: Clock, under_review: Eye, accepted: CheckCircle, declined: XCircle };
  const statusColors = { submitted: 'status-submitted', under_review: 'status-under_review', accepted: 'status-accepted', declined: 'status-declined' };

  return (
    <div data-testid="admissions-management">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <h1 className="text-2xl font-bold text-[#1B2A4A] flex items-center gap-2"><FileText className="w-6 h-6" /> Admissions</h1>
        <div className="flex gap-3">
          <Select value={filterStatus} onValueChange={v => setFilterStatus(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-40"><SelectValue placeholder="All Status" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="submitted">Submitted</SelectItem><SelectItem value="under_review">Under Review</SelectItem><SelectItem value="accepted">Accepted</SelectItem><SelectItem value="declined">Declined</SelectItem></SelectContent>
          </Select>
          <Select value={filterDiv} onValueChange={v => setFilterDiv(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-40"><SelectValue placeholder="All Divisions" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="Pre-School">Pre-School</SelectItem><SelectItem value="Primary">Primary</SelectItem><SelectItem value="High School">High School</SelectItem></SelectContent>
          </Select>
        </div>
      </div>
      <div className="bg-white rounded-xl border shadow-sm">
        <Table>
          <TableHeader><TableRow className="data-table"><TableHead>Reference</TableHead><TableHead>Child Name</TableHead><TableHead>Division</TableHead><TableHead>Grade</TableHead><TableHead>Guardian</TableHead><TableHead>Status</TableHead><TableHead>Submitted</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {apps.length === 0 ? (
              <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-400">No applications found</TableCell></TableRow>
            ) : apps.map(a => {
              const StatusIcon = statusIcons[a.status] || Clock;
              return (
                <TableRow key={a.id}>
                  <TableCell><Badge variant="outline" className="font-mono">{a.reference_number}</Badge></TableCell>
                  <TableCell className="font-semibold">{a.child_first_name} {a.child_last_name}</TableCell>
                  <TableCell>{a.division}</TableCell>
                  <TableCell>{a.grade}</TableCell>
                  <TableCell className="text-sm">{a.guardian_name}<br /><span className="text-xs text-slate-400">{a.guardian_email}</span></TableCell>
                  <TableCell><span className={`${statusColors[a.status]} text-xs px-2.5 py-1 rounded-full font-semibold inline-flex items-center gap-1`}><StatusIcon className="w-3 h-3" />{a.status.replace('_', ' ')}</span></TableCell>
                  <TableCell className="text-sm text-slate-500">{new Date(a.created_at).toLocaleDateString()}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      {a.status === 'submitted' && <Button size="sm" variant="outline" onClick={() => updateStatus(a.id, 'under_review')} className="text-xs">Review</Button>}
                      {(a.status === 'submitted' || a.status === 'under_review') && (
                        <>
                          <Button size="sm" onClick={() => updateStatus(a.id, 'accepted')} className="bg-green-600 hover:bg-green-700 text-xs text-white" data-testid={`accept-${a.id}`}>Accept</Button>
                          <Button size="sm" variant="destructive" onClick={() => updateStatus(a.id, 'declined')} className="text-xs" data-testid={`decline-${a.id}`}>Decline</Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
